import './HomePage.css';
import 'react-slideshow-image/dist/styles.css'
import React, { Fragment, useEffect, useState, memo } from 'react';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useDispatch, useSelector } from 'react-redux';
import { loadSlideshow } from '../actions/photoActions';
import { Fade } from 'react-slideshow-image';
import { Link } from 'react-router-dom';
import { trackNavigation } from '../common/utils/analytics';
import SEO from '../common/components/seo/SEO';
import { getWebsiteStructuredData } from '../common/utils/structuredData';

const Slide = memo(({ thumbUrl, mediumUrl }) => {
    const [mediumLoaded, setMediumLoaded] = useState(false);
    return (
        <div className="each-slide-effect">
            <div className="slide-frame">
                {thumbUrl && (
                    <img
                        src={thumbUrl}
                        alt=""
                        aria-hidden="true"
                        className="slide-img slide-img-thumb"
                    />
                )}
                <img
                    src={mediumUrl}
                    alt=""
                    aria-hidden="true"
                    className={`slide-img slide-img-full ${mediumLoaded ? 'slide-img-loaded' : ''}`}
                    onLoad={() => setMediumLoaded(true)}
                />
            </div>
        </div>
    );
});

const HomePage = () => {
    const dispatch = useDispatch();
    const slides = useSelector(state => state.photo.slideshowUrls);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        dispatch(loadSlideshow());
        // Trigger fade-in animation after component mounts
        setTimeout(() => setIsVisible(true), 300);
    }, [dispatch]);

    const structuredData = getWebsiteStructuredData();

    return (
        <>
            <SEO
                title="Seattle Area Landscape and Nature Photographer"
                description="Explore stunning landscape and nature photography by Valerii Tereshchenko (ValeTer), a Seattle area based photographer capturing the beauty of the Pacific Northwest, mountains, waterfalls, and natural scenes."
                keywords="Washington State landscape photographer, Nature photography prints, Pacific Northwest landscape photography, nature photographer, Landscape photography USA, Mountain photography, Waterfall photography"
                structuredData={structuredData}
            />
            <Paper className='home-page'>
                <Box className='home-hero'>
                <Fade className='slide' prevArrow={<Fragment/>} nextArrow={<Fragment/>} canSwipe={false} duration={1500} transitionDuration={700}>
                    {(slides ?? []).map((s) => (
                        <Slide key={s.id || s.mediumUrl} thumbUrl={s.thumbUrl} mediumUrl={s.mediumUrl} />
                    ))}
                </Fade>
            </Box>
            
            <Box className={`welcome-section ${isVisible ? 'visible' : ''}`}>
                <Typography variant="h4" component="h1" className='welcome-title'>
                    Welcome
                </Typography>
                <Box className='welcome-text'>
                    <Typography variant="body1" className='welcome-paragraph'>
                        I&apos;m <strong>Valerii Tereshchenko (ValeTer)</strong>, and I believe every photo tells a story.
                    </Typography>
                    <Typography variant="body1" className='welcome-paragraph'>
                        My goal isn&apos;t just to show you what a place looks like, but to share the story of a single, fleeting moment. It might be the quiet story of <em>&quot;sunlight streaming through the forest canopy&quot;</em> in the Pacific Northwest, or the epic, <em>&quot;majestic&quot;</em> narrative of a mountain in Jasper.
                    </Typography>
                    <Typography variant="body1" className='welcome-paragraph'>
                        I am a photographer and a traveler; for me, the two are inseparable. This gallery is my visual journal, a collection of the stories I&apos;ve found in the wild. I hope you can <em>&quot;feel&quot;</em> them more than just see them.
                    </Typography>
                    <Typography variant="body1" className='welcome-paragraph'>
                        My storytelling also extends to people. My portrait photography, a different chapter of my creative work, can be found at:{' '}
                        <a 
                            href="https://portraits.valeter.net" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className='portrait-link'
                        >
                            portraits.valeter.net
                        </a>
                        .
                    </Typography>
                </Box>
            </Box>
            
            <Box className='cta-section'>
                <Button
                    component={Link}
                    to="/galleries"
                    variant="outlined"
                    size="large"
                    className='cta-button'
                    onClick={() => trackNavigation('CTA: View Galleries')}
                >
                    View Galleries
                </Button>
            </Box>
        </Paper>
        </>
    );
};

export default HomePage;
